import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams, Link, useNavigate } from "react-router";
import { type ColumnDef } from "@tanstack/react-table";
import { Plus, MoreHorizontal, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { DataTable } from "@/components/ui/DataTable";
import { cn } from "@/lib/cn";

type ClientType = "adult-shelter" | "child-shelter" | "outreach" | "hotline";

type ClientStatus = "active" | "inactive" | "exited" | "waitlist";

interface Client {
  id: string;
  firstName: string;
  lastName: string;
  clientType: ClientType;
  intakeDate: string;
  status: ClientStatus;
  advocate: string;
}

interface ClientsResponse {
  clients: Client[];
  total: number;
}

const tabs = [
  { label: "All", value: "" },
  { label: "Adult Shelter", value: "adult-shelter" },
  { label: "Child Shelter", value: "child-shelter" },
  { label: "Outreach", value: "outreach" },
  { label: "Hotline", value: "hotline" },
] as const;

const statusColors: Record<ClientStatus, string> = {
  active: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/20",
  inactive: "bg-gray-50 text-gray-600 ring-1 ring-gray-500/20",
  exited: "bg-amber-50 text-amber-700 ring-1 ring-amber-600/20",
  waitlist: "bg-blue-50 text-blue-700 ring-1 ring-blue-600/20",
};

const typeLabels: Record<ClientType, string> = {
  "adult-shelter": "Adult Shelter",
  "child-shelter": "Child Shelter",
  outreach: "Outreach",
  hotline: "Hotline",
};

const placeholderClients: Client[] = [
  {
    id: "CL-1001",
    firstName: "Maria",
    lastName: "Gonzalez",
    clientType: "adult-shelter",
    intakeDate: "2026-01-15",
    status: "active",
    advocate: "Sarah M.",
  },
  {
    id: "CL-1002",
    firstName: "Jennifer",
    lastName: "Williams",
    clientType: "outreach",
    intakeDate: "2026-02-03",
    status: "active",
    advocate: "Maria L.",
  },
  {
    id: "CL-1003",
    firstName: "Ashley",
    lastName: "Brown",
    clientType: "adult-shelter",
    intakeDate: "2025-12-20",
    status: "active",
    advocate: "Sarah M.",
  },
  {
    id: "CL-1004",
    firstName: "Lisa",
    lastName: "Davis",
    clientType: "hotline",
    intakeDate: "2026-03-01",
    status: "active",
    advocate: "John K.",
  },
  {
    id: "CL-1005",
    firstName: "Emily",
    lastName: "Johnson",
    clientType: "child-shelter",
    intakeDate: "2026-01-28",
    status: "exited",
    advocate: "David R.",
  },
  {
    id: "CL-1006",
    firstName: "Sarah",
    lastName: "Martinez",
    clientType: "outreach",
    intakeDate: "2026-02-14",
    status: "active",
    advocate: "Maria L.",
  },
  {
    id: "CL-1007",
    firstName: "Amy",
    lastName: "Taylor",
    clientType: "adult-shelter",
    intakeDate: "2026-03-10",
    status: "waitlist",
    advocate: "Sarah M.",
  },
  {
    id: "CL-1008",
    firstName: "Rachel",
    lastName: "Anderson",
    clientType: "hotline",
    intakeDate: "2026-03-12",
    status: "active",
    advocate: "John K.",
  },
];

export function ClientListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get("type") ?? "") as ClientType | "";
  const searchQuery = searchParams.get("q") ?? "";

  const { data, isLoading, refetch, isFetching } = useQuery<ClientsResponse>({
    queryKey: ["clients", activeTab, searchQuery],
    queryFn: async () => {
      try {
        const params: Record<string, string> = {};
        if (activeTab) params["type"] = activeTab;
        if (searchQuery) params["query"] = searchQuery;

        // The API returns { data: [...], total?, pagination? }
        const raw = await api.get<{
          data?: Array<{
            id: number | string;
            personType?: string;
            clientType?: string;
            firstName: string;
            lastName: string;
            intakeDate?: string;
            internalId?: string;
            status?: string;
            advocate?: string;
          }>;
          clients?: Client[];
          total?: number;
        }>("/clients", { params });

        // If API already returns { clients }, use it directly
        if (raw.clients) {
          return raw as ClientsResponse;
        }

        // Map API format to frontend format
        const personTypeMap: Record<string, ClientType> = {
          shelter: "adult-shelter",
          child: "child-shelter",
          outreach: "outreach",
          hotline: "hotline",
          "adult-shelter": "adult-shelter",
          "child-shelter": "child-shelter",
        };

        const statusMap: Record<string, Client["status"]> = {
          active: "active",
          inactive: "inactive",
          exited: "exited",
          departed: "exited",
          waitlist: "waitlist",
          incomplete: "inactive",
        };

        const clients: Client[] = (raw.data || []).map((p) => ({
          id: p.internalId || String(p.id),
          firstName: p.firstName || "",
          lastName: p.lastName || "",
          clientType: personTypeMap[p.personType || p.clientType || ""] || "outreach",
          intakeDate: p.intakeDate || "",
          status: statusMap[p.status || "active"] || "active",
          advocate: p.advocate || "",
        }));

        // Apply client-side filter for tab if API doesn't filter
        let filtered = clients;
        if (activeTab) {
          filtered = filtered.filter((c) => c.clientType === activeTab);
        }

        return { clients: filtered, total: filtered.length };
      } catch {
        // Fallback to placeholder data during development
        let filtered = placeholderClients;
        if (activeTab) {
          filtered = filtered.filter((c) => c.clientType === activeTab);
        }
        if (searchQuery) {
          const lower = searchQuery.toLowerCase();
          filtered = filtered.filter(
            (c) =>
              c.firstName.toLowerCase().includes(lower) ||
              c.lastName.toLowerCase().includes(lower) ||
              c.id.toLowerCase().includes(lower),
          );
        }
        return { clients: filtered, total: filtered.length };
      }
    },
    placeholderData: {
      clients: activeTab
        ? placeholderClients.filter((c) => c.clientType === activeTab)
        : placeholderClients,
      total: placeholderClients.length,
    },
  });

  const columns = useMemo<ColumnDef<Client, unknown>[]>(
    () => [
      {
        accessorKey: "id",
        header: "ID",
        cell: ({ row }) => (
          <Link
            to={`/clients/${row.original.id}`}
            className="font-medium text-primary hover:underline"
          >
            {row.original.id}
          </Link>
        ),
      },
      {
        id: "name",
        header: "Name",
        accessorFn: (row) => `${row.lastName}, ${row.firstName}`,
        cell: ({ row }) => (
          <Link
            to={`/clients/${row.original.id}`}
            className="font-medium hover:underline"
          >
            {row.original.lastName}, {row.original.firstName}
          </Link>
        ),
      },
      {
        accessorKey: "clientType",
        header: "Type",
        cell: ({ row }) => typeLabels[row.original.clientType],
      },
      {
        accessorKey: "intakeDate",
        header: "Intake Date",
        cell: ({ row }) =>
          new Date(row.original.intakeDate).toLocaleDateString(),
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => (
          <span
            className={cn(
              "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize",
              statusColors[row.original.status],
            )}
          >
            {row.original.status}
          </span>
        ),
      },
      {
        accessorKey: "advocate",
        header: "Advocate",
      },
      {
        id: "actions",
        header: "",
        cell: ({ row }) => (
          <Link to={`/clients/${row.original.id}`}>
            <Button variant="ghost" size="icon">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </Link>
        ),
        enableSorting: false,
      },
    ],
    [],
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Clients</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage client records across all programs
            {data?.total !== undefined && (
              <span className="ml-1">({data.total} total)</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={() => void refetch()}
            disabled={isFetching}
          >
            <RefreshCw className={cn("h-4 w-4", isFetching && "animate-spin")} />
          </Button>
          <Button className="gap-2" onClick={() => void navigate("/clients/new")}>
            <Plus className="h-4 w-4" />
            New Client
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b">
        <nav className="-mb-px flex gap-4">
          {tabs.map((tab) => (
            <button
              key={tab.value}
              onClick={() => {
                if (tab.value) {
                  setSearchParams({ type: tab.value });
                } else {
                  setSearchParams({});
                }
              }}
              className={cn(
                "whitespace-nowrap border-b-2 px-1 pb-3 text-sm font-medium transition-colors",
                activeTab === tab.value
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:border-border hover:text-foreground",
              )}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        data={data?.clients ?? []}
        searchColumn="name"
        searchPlaceholder="Search by name..."
        isLoading={isLoading}
        enableRowSelection
      />
    </div>
  );
}
